import { ArrowRight, Award, CheckCircle2, Flame, Medal, ShieldCheck, Trophy } from 'lucide-react'
import { Link } from 'react-router-dom'
import { EventHero } from '../components/EventHero'
import { InlineError, SectionHeader, Skeleton, UserAvatar } from '../components/ui'
import { useAuth } from '../context/AuthContext'
import { useLeaderboard, useMyPredictions, useUpcomingEvent } from '../hooks/queries'
import { countryFlag } from '../lib/format'

export default function HomePage() {
  const { user } = useAuth()
  const eventQuery = useUpcomingEvent()
  const predictionsQuery = useMyPredictions()
  const leaderboardQuery = useLeaderboard('all_time')

  if (eventQuery.isLoading) {
    return <div className="page"><Skeleton className="skeleton--hero" /><Skeleton className="skeleton--section" /></div>
  }

  if (eventQuery.error || !eventQuery.data) {
    return <div className="page"><InlineError message={eventQuery.error?.message} /></div>
  }

  const event = eventQuery.data
  const fightIds = new Set(event.fights.map((fight) => fight.id))
  const completed = (predictionsQuery.data ?? []).filter((prediction) => fightIds.has(prediction.fightId)).length
  const leaders = leaderboardQuery.data?.slice(0, 3) ?? []

  return (
    <div className="page home-page">
      <EventHero event={event} completed={completed} total={event.fights.length} isGuest={!user} />

      <section className="home-grid">
        <div className="surface-card home-ranking">
          <SectionHeader
            eyebrow="Temporada 2026"
            title="No topo da liga"
            action={<Link className="text-link" to="/ranking">Ranking completo <ArrowRight size={16} /></Link>}
          />
          <div className="podium-list">
            {leaders.map((entry) => (
              <Link key={entry.userId} to={`/u/${entry.username}`} className="podium-row">
                <span className={`podium-position podium-position--${entry.position}`}>{entry.position}</span>
                <UserAvatar name={entry.displayName} src={entry.avatarUrl} size="sm" />
                <div className="podium-user">
                  <strong>{entry.displayName} {countryFlag(entry.countryCode)}</strong>
                  <span>@{entry.username} · {entry.perfectPicks} cravadas</span>
                </div>
                <strong className="podium-points">{entry.points}<small> pts</small></strong>
              </Link>
            ))}
          </div>
        </div>

        <div className="surface-card weekly-mission">
          <div className="weekly-mission__icon"><Flame size={26} /></div>
          <span className="eyebrow">{user ? 'Sequência atual' : 'Entre na disputa'}</span>
          <h2>{user ? '4 eventos pontuando' : 'Crie seu perfil na liga'}</h2>
          <p>{user ? 'Complete o próximo card para manter sua melhor sequência do ano viva.' : 'Faça seus palpites, dispute o ranking e desafie seus amigos sem pagar nada.'}</p>
          {user ? <div className="streak-dots" aria-label="Quatro eventos consecutivos">
            {[1, 2, 3, 4, 5].map((item) => <span key={item} className={item <= 4 ? 'is-active' : ''}>{item <= 4 ? <CheckCircle2 size={15} /> : item}</span>)}
          </div> : null}
          <Link to="/palpites" className="button button--secondary">{user ? 'Completar card' : 'Criar conta grátis'} <ArrowRight size={17} /></Link>
        </div>
      </section>

      <section className="home-achievements">
        <SectionHeader eyebrow={user ? 'Seu desempenho' : 'Dentro da liga'} title={user ? 'Conquistas recentes' : 'Conquistas que você desbloqueia'} />
        <div className="achievement-grid">
          <article className="achievement-card achievement-card--coral">
            <span><Award size={23} /></span>
            <div><strong>Na mosca</strong><small>5 palpites perfeitos</small></div>
          </article>
          <article className="achievement-card achievement-card--indigo">
            <span><Medal size={23} /></span>
            <div><strong>Pódio do card</strong><small>3º lugar no último evento</small></div>
          </article>
          <article className="achievement-card achievement-card--gold">
            <span><Trophy size={23} /></span>
            <div><strong>Casca-grossa</strong><small>100 lutas pontuadas</small></div>
          </article>
        </div>
      </section>

      <aside className="fair-play-note">
        <ShieldCheck size={22} />
        <div>
          <strong>Competição limpa, só pela resenha.</strong>
          <span>A LIGA UVM não envolve apostas, depósitos ou prêmios em dinheiro.</span>
        </div>
      </aside>
    </div>
  )
}
